import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    // Animate header in on mount
    const t = setTimeout(() => setIsLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { name: 'Início', path: '/' },
    { name: 'Portfólio', path: '/portfolio' },
    { name: 'Quem Somos', path: '/quem-somos' },
    { name: 'Blog', path: '/blog' },
    { name: 'Locais', path: '/locais' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contato', path: '/contato' },
  ];

  const serviceLinks = [
    { name: 'Todos os Serviços', path: '/servicos', icon: 'ri-apps-line' },
    { name: 'Palestra Silenciosa em Eventos', path: '/palestra-silenciosa-eventos', icon: 'ri-headphone-line' },
    { name: 'Festa Silenciosa (Silent Disco)', path: '/festa-silenciosa', icon: 'ri-music-line' },
    { name: 'Aula Silenciosa', path: '/aula-silenciosa', icon: 'ri-book-open-line' },
    { name: 'Aluguel de Fones Sem Fio', path: '/aluguel-fones-sem-fio', icon: 'ri-wireless-charging-line' },
    { name: 'Fones para Estandes em Feiras', path: '/aluguel-fones-estande-sao-paulo', icon: 'ri-store-2-line' },
    { name: 'Ativação de Experiência', path: '/ativacao-de-experiencia-com-fones-sao-paulo', icon: 'ri-magic-line' },
  ];

  const isServicesActive =
    location.pathname === '/servicos' || location.pathname === '/palestra-silenciosa-eventos' || location.pathname === '/festa-silenciosa' || location.pathname === '/silent-disco' || location.pathname === '/aula-silenciosa' || location.pathname === '/silent-class';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white dark:bg-gray-900 shadow-md' : 'bg-transparent'
      } ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
      style={{ transition: 'transform 0.5s cubic-bezier(.22,.68,0,1.2), opacity 0.5s ease, background-color 0.3s, box-shadow 0.3s' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group" aria-label="STIB - Palestra Silenciosa">
            <img
              src="/img/stib/logo-stib-3d.png"
              alt="STIB - Palestra Silenciosa"
              className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {/* Início */}
            <Link
              to="/"
              className={`text-sm font-medium transition-all duration-200 whitespace-nowrap relative ${
                location.pathname === '/'
                  ? isScrolled ? 'text-primary dark:text-primary' : 'text-white font-semibold'
                  : isScrolled ? 'text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary' : 'text-white/90 hover:text-white'
              }`}
            >
              Início
              {location.pathname === '/' && (
                <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full animate-fade-in" />
              )}
            </Link>

            {/* Serviços Dropdown */}
            <div className="relative" ref={servicesRef}>
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer relative ${
                  isServicesActive
                    ? isScrolled ? 'text-primary dark:text-primary' : 'text-white font-semibold'
                    : isScrolled ? 'text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary' : 'text-white/90 hover:text-white'
                }`}
                aria-haspopup="true"
                aria-expanded={isServicesOpen}
              >
                Serviços
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`}></i>
                </div>
                {isServicesActive && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full animate-fade-in" />
                )}
              </button>

              {isServicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-fade-in-down" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.10)' }}>
                  <div className="p-2">
                    {serviceLinks.map((sl) => (
                      <Link
                        key={sl.path}
                        to={sl.path}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer hover:translate-x-1 ${
                          location.pathname === sl.path
                            ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary/10 dark:bg-primary/20">
                          <i className={`${sl.icon} text-primary text-base`}></i>
                        </div>
                        {sl.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Remaining links */}
            {navLinks.filter(l => l.path !== '/').map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-all duration-200 whitespace-nowrap relative ${
                  location.pathname === link.path
                    ? isScrolled ? 'text-primary dark:text-primary' : 'text-white font-semibold'
                    : isScrolled ? 'text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary' : 'text-white/90 hover:text-white'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full animate-fade-in" />
                )}
              </Link>
            ))}

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200 hover:scale-110 ${
                isScrolled
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              aria-label={
                theme === 'auto'
                  ? 'Tema automático (clique para tema claro)'
                  : theme === 'light'
                  ? 'Tema claro (clique para tema escuro)'
                  : 'Tema escuro (clique para tema automático)'
              }
              title={theme === 'auto' ? 'Automático' : theme === 'light' ? 'Claro' : 'Escuro'}
            >
              {theme === 'auto' ? (
                <i className="ri-contrast-2-line text-xl"></i>
              ) : theme === 'light' ? (
                <i className="ri-sun-line text-xl animate-fade-in"></i>
              ) : (
                <i className="ri-moon-line text-xl animate-fade-in"></i>
              )}
            </button>

            <Link
              to="/orcamento"
              className="px-6 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap text-white hover:opacity-90 hover:scale-105"
              style={{ background: '#18c7f4', color: '#384774', fontWeight: 700 }}
            >
              Solicitar Orçamento
            </Link>
          </nav>

          {/* Mobile Menu Button & Theme Toggle */}
          <div className="lg:hidden flex items-center space-x-3">
            <button
              onClick={toggleTheme}
              className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                isScrolled ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' : 'bg-white/10 text-white'
              }`}
              aria-label={
                theme === 'auto'
                  ? 'Tema automático (clique para tema claro)'
                  : theme === 'light'
                  ? 'Tema claro (clique para tema escuro)'
                  : 'Tema escuro (clique para tema automático)'
              }
              title={theme === 'auto' ? 'Automático' : theme === 'light' ? 'Claro' : 'Escuro'}
            >
              {theme === 'auto' ? (
                <i className="ri-contrast-2-line text-xl"></i>
              ) : theme === 'light' ? (
                <i className="ri-sun-line text-xl"></i>
              ) : (
                <i className="ri-moon-line text-xl"></i>
              )}
            </button>

            {/* Animated Hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200 ${
                isScrolled ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' : 'bg-white/10 text-white'
              }`}
              aria-label="Toggle menu"
            >
              <div className="relative w-5 h-4 flex flex-col justify-between items-center">
                <span className={`block w-full h-[2px] rounded-full transition-all duration-300 ease-out ${isMobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''} ${isScrolled ? 'bg-gray-700 dark:bg-gray-300' : 'bg-white'}`} />
                <span className={`block w-full h-[2px] rounded-full transition-all duration-300 ease-out ${isMobileMenuOpen ? 'opacity-0 scale-0' : ''} ${isScrolled ? 'bg-gray-700 dark:bg-gray-300' : 'bg-white'}`} />
                <span className={`block w-full h-[2px] rounded-full transition-all duration-300 ease-out ${isMobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''} ${isScrolled ? 'bg-gray-700 dark:bg-gray-300' : 'bg-white'}`} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 animate-fade-in-down">
          <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === '/' ? 'bg-primary/10 dark:bg-primary/20 text-primary' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              Início
            </Link>

            {/* Services group */}
            <div className="px-4 pt-3 pb-1">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2">Serviços</p>
            </div>
            {serviceLinks.map((sl) => (
              <Link
                key={sl.path}
                to={sl.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === sl.path ? 'bg-primary/10 dark:bg-primary/20 text-primary' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${sl.icon} text-primary text-sm`}></i>
                </div>
                {sl.name}
              </Link>
            ))}

            {navLinks.filter(l => l.path !== '/').map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === link.path ? 'bg-primary/10 dark:bg-primary/20 text-primary' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/orcamento"
              className="block px-4 py-3 text-white rounded-lg text-sm font-bold text-center transition-colors whitespace-nowrap hover:opacity-90"
              style={{ background: '#18c7f4', color: '#384774' }}
            >
              Solicitar Orçamento
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;